<script setup lang="ts">
import { computed, onMounted, ref } from "vue"
import { useI18n } from "vue-i18n"

import Button from "@/components/shared/Button.vue"
import Card from "@/components/shared/Card.vue"
import CardContent from "@/components/shared/CardContent.vue"
import CardTitle from "@/components/shared/CardTitle.vue"
import InfoRow from "@/components/shared/InfoRow.vue"
import InfoTable from "@/components/shared/InfoTable.vue"
import LayoutAuthed from "@/components/shared/LayoutAuthed.vue"
import store from "@/plugins/store"
import partnerService from "@/services/partner"
import type { NotificationPhone, Partner } from "@/types/partner.type"
import { reportSentry } from "@/utils/sentry"

const { t } = useI18n()

const partner = computed<Partner | null>(() => store.getters.partner)
const phones = ref<NotificationPhone[]>([])
const emails = ref<string[]>([])

async function loadData() {
  const data = await partnerService.notification()

  phones.value = data.phones
  emails.value = data.emails
}

// initial load
onMounted(async () => {
  try {
    await loadData()
  } catch (error) {
    console.error(error)
    reportSentry(error)
  }
})
</script>

<template>
  <LayoutAuthed :title="$t('nav.settings')">
    <template v-if="partner">
      <Card>
        <CardTitle>
          <template #default>
            {{ t("partner.businessInfo") }}
          </template>
          <template #actions>
            <!--            <Button type="link" path="#" :text="$t('shared.edit')" color="warning" size="sm"/>-->
          </template>
        </CardTitle>
        <CardContent>
          <InfoTable>
            <InfoRow :name="$t('partner.businessName')">{{ partner.name }}</InfoRow>
            <InfoRow :name="$t('partner.country')">{{ partner.country.name }}</InfoRow>
            <InfoRow :name="$t('partner.taxId')">{{ partner.businessNumber }}</InfoRow>
          </InfoTable>
        </CardContent>
      </Card>
      <Card>
        <CardTitle>
          <template #default>
            {{ t("partner.contact") }}
          </template>
          <template #actions>
            <!--            <Button type="link" path="#" :text="$t('shared.edit')" color="warning" size="sm"/>-->
          </template>
        </CardTitle>
        <CardContent>
          <div class="row">
            <div class="col-12 col-lg-6 col-xxl-6">
              <InfoTable>
                <InfoRow :name="$t('partner.contactName')">{{ partner.contactName }}</InfoRow>
                <InfoRow :name="$t('partner.mobilePhone')">
                  +{{ partner.mobilePhone.country.callingCode }} {{ partner.mobilePhone.number }}
                </InfoRow>
                <InfoRow v-if="partner.phone" :name="$t('partner.phone')">
                  +{{ partner.phone.country.callingCode }} {{ partner.phone.number }}
                </InfoRow>
                <InfoRow :name="$t('partner.email')">{{ partner.email }}</InfoRow>
              </InfoTable>
            </div>
            <div class="col-12 col-lg-6 col-xxl-6">
              <InfoTable>
                <InfoRow :name="$t('partner.address')">{{ partner.factoryAddress }}</InfoRow>
                <InfoRow v-if="partner.ddmAddress" :name="$t('partner.ddmAddress')">{{ partner.ddmAddress }}</InfoRow>
              </InfoTable>
            </div>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardTitle>
          <template #default>
            {{ t("partner.payoutSettings") }}
          </template>
          <template #actions> </template>
        </CardTitle>
        <CardContent>
          <InfoTable>
            <InfoRow v-if="partner.bankAccount" :name="$t('partner.bankAccount.bankName')">
              {{ partner.bankAccount.bankName }}
            </InfoRow>
            <InfoRow v-if="partner.bankAccount" :name="$t('partner.bankAccount.accountNumber')">
              {{ partner.bankAccount.accountNumber }}
            </InfoRow>
            <InfoRow v-if="partner.bankAccount" :name="$t('partner.bankAccount.accountHolder')">
              {{ partner.bankAccount.accountHolder }}
            </InfoRow>
          </InfoTable>
        </CardContent>
      </Card>
      <Card>
        <CardTitle>
          <template #default>
            {{ t("partner.notificationSettings") }}
          </template>
          <template #actions>
            <Button
              type="link"
              path="/settings/edit_notification"
              :text="$t('shared.edit')"
              color="warning"
              size="sm"
            />
          </template>
        </CardTitle>
        <CardContent>
          <div class="row">
            <div class="col-12 col-lg-6 col-xxl-6">
              <InfoTable>
                <InfoRow v-for="(phone, index) in phones" :name="$t('partner.mobilePhone') + '#' + (index + 1)">
                  +{{ phone.country.callingCode }} {{ phone.number }}
                </InfoRow>
              </InfoTable>
            </div>
            <div class="col-12 col-lg-6 col-xxl-6">
              <InfoTable>
                <InfoRow v-for="(email, index) in emails" :name="$t('partner.email') + '#' + (index + 1)">
                  {{ email }}
                </InfoRow>
              </InfoTable>
            </div>
          </div>
        </CardContent>
      </Card>
    </template>
  </LayoutAuthed>
</template>

<style scoped></style>
